import React, { useState, useContext } from 'react';
import { PropTypes } from 'prop-types';
import gql from 'graphql-tag';
import { range } from 'lodash';
import { trackEvent } from '../Analytics';
import SSRAwareQuery from '../SSRAwareQuery';

import moment from '@g4/moment-utc';

import withQueryParams from '../withQueryParams';
import TranslationsContext from '../TranslationsContext';

import {
  DESTINATION_FIELD,
  CHECK_IN_FIELD,
  CHECK_OUT_FIELD,
  ROOMS_FIELD,
  ROOM_PARTY,
  CHILDREN_AGES,
  ADULTS_FIELD,
  CHILDREN_FIELD,
} from '../../utils/fieldsNames';
import { GA4_EVENTS } from '../../utils/constants/trackingConstants';

export const query = gql`
  query hotelsDestination {
    application(name: HOTELONLYBOOKING) {
      ... on HotelOnlyBooking {
        configurations {
          maxRoomsCount
          maxAdultsCount
          maxChildrenAge
          maxChildrenCount
          defaultChildrenCount
          hotelsPerPage
        }
        sortConfigurations {
          default
        }
      }
    }
    hotelLocations {
      id
      code
      name
      title
      isMain
    }
  }
`;

const defaultChildrenCount = configs =>
  range(configs.defaultChildrenCount).map(child => -1);

const formFriendlyParams = (search, destinations) => {
  const destination =
    destinations.find(d => d.code === search[DESTINATION_FIELD] && d.isMain) ||
    {};
  const room = search[ROOM_PARTY].map(room => ({
    ...room,
    [CHILDREN_FIELD]: room[CHILDREN_AGES] ? room[CHILDREN_AGES].length : 0,
    [CHILDREN_AGES]: room[CHILDREN_AGES] ? room[CHILDREN_AGES] : [],
  }));
  return {
    [DESTINATION_FIELD]: destination.id,
    [CHECK_IN_FIELD]: moment(search[CHECK_IN_FIELD]),
    [CHECK_OUT_FIELD]: moment(search[CHECK_OUT_FIELD]),
    [ROOMS_FIELD]: search[ROOM_PARTY].length,
    [ROOM_PARTY]: room,
  };
};

const SearchFromDataProvider = ({ searchParams, children }) => {
  const [isEditMode, setState] = useState(false);
  const translations = useContext(TranslationsContext);

  const toggleForm = () => {
    if (!isEditMode) {
      trackEvent(GA4_EVENTS.SITE_CLICK_TRACKING, 'Edit Search', 'Edit');
    }
    setState(!isEditMode);
  };
  return (
    <SSRAwareQuery query={query}>
      {({ loading, data, error }) => {
        if (loading) {
          return null;
        }
        if (error) {
          throw error;
        }
        const { configurations } = data.application;
        const { searchSummary, searchWidget } = translations;
        const defaultSort = data.application.sortConfigurations.default;
        const destinations = data.hotelLocations;
        const { hotelSearchCriteria } = searchParams;
        const initialSearch = Object.values(hotelSearchCriteria).every(
          param => param === undefined
        );

        const defaultValues = initialSearch
          ? {
              [DESTINATION_FIELD]: '',
              [CHECK_IN_FIELD]: '',
              [CHECK_OUT_FIELD]: '',
              [ROOMS_FIELD]: 1,
              [ROOM_PARTY]: [
                {
                  [ADULTS_FIELD]: 1,
                  [CHILDREN_FIELD]: defaultChildrenCount(configurations).length,
                  [CHILDREN_AGES]: defaultChildrenCount(configurations),
                },
              ],
            }
          : formFriendlyParams(hotelSearchCriteria, destinations);

        const summaryData = {
          translations: searchSummary,
          destinations,
        };

        const formData = {
          defaultValues,
          configurations,
          defaultSort,
          destinations,
          translations: searchWidget,
        };

        return children({
          summaryData,
          formData,
          isEditMode,
          toggleForm,
        });
      }}
    </SSRAwareQuery>
  );
};

SearchFromDataProvider.propTypes = {
  searchParams: PropTypes.object.isRequired,
  children: PropTypes.func.isRequired,
};

export default withQueryParams(SearchFromDataProvider);
